import { graphql, useFragment } from "react-relay";

import { sizeToWidthAndHeight } from "./sizeToWidthAndHeight";
import { useWidgetToPosSingleSize_widget$key } from "./__generated__/useWidgetToPosSingleSize_widget.graphql";
import { useWidgetToPos_widget$key } from "./__generated__/useWidgetToPos_widget.graphql";

function assertNever(value: never, noThrow?: boolean): never {
  throw Error(
    `widgetToLayoutSize not supported type:  ${(value as any)?.__typename}`
  );
}

export type WidgetPos = {
  y: number;
  x: number;
  h: number;
  w: number;
  c: number;
  id: string;
  kind: string;
};

export type WidgetPosWithSubs = WidgetPos & {
  positionX: number;
  sub:
    | {
        y: number;
        x: number;
        h: number;
        w: number;
        id: string;
        c: number;
      }[]
    | undefined;
};

export type AnonymousWidgetPos = Pick<WidgetPos, "c" | "x" | "w" | "h" | "y">;

export function useWidgetsToPos(widgetKeys: useWidgetToPos_widget$key) {
  const widgets = useFragment(
    graphql`
      fragment useWidgetToPos_widget on Widget @relay(plural: true) {
        id
        positionX
        positionY
        ... on WidgetOverview {
          overviewSize
        }
        ... on WidgetNote {
          noteSize
        }
        ... on WidgetTaskSingle {
          taskSingleSize
        }
        ... on WidgetWeather {
          size
        }
        ... on WidgetIntegrationTurnpike {
          turnpikeSize
        }
        ... on WidgetSpace {
          subWidgets {
            id
            positionX
            positionY
            height
          }
        }
        __typename
      }
    `,
    widgetKeys
  );

  return widgets.map((widget): WidgetPosWithSubs => {
    const shared = {
      id: widget.id,
      kind: widget.__typename,
      x: widget.positionX % 2,
      y: widget.positionY,
      c: Math.floor(widget.positionX / 2),
      positionX: widget.positionX,
      sub:
        widget.__typename === "WidgetSpace"
          ? widget.subWidgets?.map((sb) => ({
              y: widget.positionY + sb.positionY,
              id: sb.id,
              c: Math.floor(widget.positionX / 2),
              w: 2,
              h: sb.height,
              x: widget.positionX % 2,
            }))
          : [],
    };
    switch (widget.__typename) {
      case "WidgetSpace":
      case "WidgetIntegrationFlourishPraise":
      case "WidgetFile":
      case "WidgetIssueSummary":
      case "WidgetTodoSummary":
      case "WidgetEmployeeSchedule":
      case "WidgetEmployeeLeave":
      case "WidgetIntegrationPushAppy":
      case "WidgetIssueOverview":
      case "WidgetRoutineOverview":
      case "WidgetTodoOverview":
      case "WidgetSignalOverview":
      case "WidgetEmployeeRoleOverview":
      case "WidgetTeamSchedule":
      case "WidgetCalendarOverview":
      case "WidgetIntegrationIpool":
      case "WidgetCalendar":
      case "WidgetKPI":
      case "WidgetList":
      case "WidgetTable":
        return {
          ...shared,
          ...sizeToWidthAndHeight(widget.overviewSize!),
        };
      case "WidgetNote":
        return {
          ...shared,
          ...sizeToWidthAndHeight(widget.noteSize!),
        };

      case "WidgetTaskCollection":
        return { ...shared, kind: widget.__typename, w: 2, h: 1 };

      case "WidgetRoutinePositivePercentage":
        return { ...shared, kind: widget.__typename, w: 2, h: 1 };
      case "WidgetTaskSingle":
        return {
          ...shared,
          ...sizeToWidthAndHeight(widget.taskSingleSize!),
        };
      case "WidgetWeather":
        return widget.size === "ONE_X_ONE"
          ? { ...shared, w: 1, h: 1 }
          : { ...shared, w: 2, h: 1 };
      case "WidgetTaskMultiple":
        return { ...shared, w: 2, h: 1 };
      case "WidgetIntegrationTurnpike":
        return widget.turnpikeSize === "TWO_X_ONE"
          ? { ...shared, w: 2, h: 1 }
          : { ...shared, w: 2, h: 2 };
      case "WidgetNewsfeed":
        return {
          ...shared,
          ...sizeToWidthAndHeight(widget.overviewSize!),
        };
      default:
        throw new Error(
          "useWidgetToLayoutSize not supported type: " + widget.__typename
        );
    }
  });
}

function useSingleWidgetSize(
  widgetKeys: useWidgetToPosSingleSize_widget$key,
  overrides?: { [typename: string]: { w: number; h: number } }
) {
  const widget = useFragment(
    graphql`
      fragment useWidgetToPosSingleSize_widget on Widget {
        __typename
        ... on WidgetOverview {
          overviewSize
        }
        ... on WidgetNote {
          noteSize
        }
        ... on WidgetTaskSingle {
          taskSingleSize
        }
        ... on WidgetWeather {
          size
        }
        ... on WidgetIntegrationTurnpike {
          turnpikeSize
        }
      }
    `,
    widgetKeys
  );

  if (overrides?.[widget.__typename]) {
    return overrides[widget.__typename];
  }

  switch (widget.__typename) {
    case "WidgetSpace":
    case "WidgetIntegrationFlourishPraise":
    case "WidgetFile":
    case "WidgetIssueSummary":
    case "WidgetEmployeeSchedule":
    case "WidgetEmployeeLeave":
    case "WidgetIntegrationPushAppy":
    case "WidgetIssueOverview":
    case "WidgetRoutineOverview":
    case "WidgetTodoOverview":
    case "WidgetSignalOverview":
    case "WidgetEmployeeRoleOverview":
    case "WidgetTeamSchedule":
    case "WidgetIntegrationIpool":
    case "WidgetCalendar":
    case "WidgetKPI":
    case "WidgetList":
    case "WidgetTable":
      return sizeToWidthAndHeight(widget.overviewSize!);

    case "WidgetNote":
      return sizeToWidthAndHeight(widget.noteSize!);

    case "WidgetTaskCollection":
      return { w: 2, h: 1 };

    case "WidgetRoutinePositivePercentage":
      return { w: 2, h: 1 };
    case "WidgetTaskSingle":
      return sizeToWidthAndHeight(widget.taskSingleSize!);

    case "WidgetWeather":
      return widget.size === "ONE_X_ONE" ? { w: 1, h: 1 } : { w: 2, h: 1 };
    case "WidgetTaskMultiple":
      return { w: 2, h: 1 };
    case "WidgetIntegrationTurnpike":
      return widget.turnpikeSize === "TWO_X_ONE"
        ? { w: 2, h: 1 }
        : { w: 2, h: 2 };
    default:
      throw new Error(
        "useWidgetToLayoutSize not supported type: " + widget.__typename
      );
  }
}
